import axios from 'axios';
import { getToken as getCsrfToken, FIELD_NAME as CSRF_FIELD_NAME } from 'Libs/helpers/csrf';

const DEBUG_PREFIX = '[SselTrackingService]';

const CHECKOUT_PAGE_TYPES = Object.freeze({
  PAYMENT: 'checkout2',
  CONFIRMATION: 'checkout3',
});
// For consistency with other tracking events
const DESTINATION_URL_BY_PAGE_TYPE = {
  [CHECKOUT_PAGE_TYPES.PAYMENT]: '/shop/checkout/details/payment',
  [CHECKOUT_PAGE_TYPES.CONFIRMATION]: '/shop/checkout/details/confirmation',
};

const resolveDestinationUrl = (pageType) => {
  if (typeof window === 'undefined' || !pageType) return null;

  const destinationPath = DESTINATION_URL_BY_PAGE_TYPE[pageType];
  if (!destinationPath) return null;

  return `${window.location.origin}${destinationPath}`;
};

export const trackPageType = (pageType) => {
  const url = resolveDestinationUrl(pageType);

  const payload = {
    page_type: pageType,
    url,
  };

  if (!pageType || !url) {
    // eslint-disable-next-line no-console
    console.log(`${DEBUG_PREFIX} skipped (invalid params) ‼️ ❌`, payload);
    return;
  }

  axios
    .post('/ssel/t', payload, {
      headers: {
        'Content-Type': 'application/json',
        'x-event-type': 'page_view',
      },
    })
    .catch((error) => {
      // swallow errors: tracking must not break checkout flow
      // eslint-disable-next-line no-console
      console.error(`${DEBUG_PREFIX} request failed ❌`, { payload, error });
    });
};

export const trackExperimentViewed = ({ experimentId, variationId, pageViewEventId }) => {
  if (!experimentId || !pageViewEventId) {
    // eslint-disable-next-line no-console
    console.log(`${DEBUG_PREFIX} experiment tracking skipped (missing experimentId or pageViewEventId)`);
    return;
  }

  const payload = {
    gb_experiment_id: experimentId,
    gb_variation_id: variationId,
    reference_event_id: pageViewEventId,
  };

  axios
    .post('/ssel/t', payload, {
      headers: {
        'Content-Type': 'application/json',
        'x-event-type': 'experiment_viewed',
      },
    })
    .catch((error) => {
      // swallow errors: tracking must not block rendering
      // eslint-disable-next-line no-console
      console.error(`${DEBUG_PREFIX} experiment tracking failed ❌`, { payload, error });
    });
};

export const trackClick = ({
  clickType,
  referenceEventId,
  targetUrl,
  label,
  position,
  context,
  pageType,
} = {}) => {
  if (!clickType) {
    // eslint-disable-next-line no-console
    console.log(`${DEBUG_PREFIX} click tracking skipped (missing clickType)`);
    return;
  }

  const payload = {
    url: typeof window === 'undefined' ? undefined : window.location.href,
    referer: typeof document === 'undefined' ? undefined : document.referrer,
    reference_event_id: referenceEventId,
    click_type: clickType,
    target_url: targetUrl,
    label,
    position,
    context,
    page_type: pageType,
    // sendBeacon cannot set custom headers, so the CSRF token travels in the
    // body instead of the x-csrf-token header the axios/fetch interceptors use.
    [CSRF_FIELD_NAME]: getCsrfToken(),
  };

  if (
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    // eslint-disable-next-line no-console
    console.error('%s click tracking unavailable (no sendBeacon) ❌', DEBUG_PREFIX, payload);
    return;
  }

  const body = JSON.stringify(payload);

  const blob = new Blob([body], {
    type: 'application/json',
  });
  const queued = navigator.sendBeacon('/ssel/t?event_type=click', blob);

  if (!queued) {
    // eslint-disable-next-line no-console
    console.error('%s click tracking beacon was not queued ❌', DEBUG_PREFIX, payload);
  }
};

export const trackCheckoutStep = (step) => {
  trackPageType(step);
};

export const trackCheckoutPayment = () => {
  trackCheckoutStep(CHECKOUT_PAGE_TYPES.PAYMENT);
};

export const trackCheckoutConfirmation = () => {
  trackCheckoutStep(CHECKOUT_PAGE_TYPES.CONFIRMATION);
};
